import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import html2pdf from "html2pdf.js";
import { technicianMfiApi } from "./api";
import SmartHealthReport from "./mfiReport/SmartHealthReport";
import { normalizeSmartReport } from "./mfiReport/normalizeSmartReport";

function buildDirectReportFilename(report) {
  const name = String(report?.member?.name || "Member").trim().replace(/\s+/g, "_").replace(/[^A-Za-z0-9_.-]/g, "") || "Member";
  const code = String(report?.member?.member_id || report?.member?.id || "member").trim() || "member";
  return `Smart_Report_${name}_${code}.pdf`;
}

const COLORS = {
  aquaBlue: "#11a8a4",
  grassGreen: "#7ed957",
  darkGrey: "#3c3b3f",
  vividPurple: "#9440dd",
  white: "#ffffff",
  lightGrey: "#f3f4f6",
  mediumGrey: "#e5e7eb",
  darkText: "#1f2937",
  mediumText: "#6b7280",
  lightText: "#9ca3af",
};

function formatDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function addressLine(a) {
  if (!a) return "—";
  return [a.address_line_1, a.address_line_2, a.village, a.gram_panchayat, a.block, a.district, a.state, a.pincode].filter(Boolean).join(", ") || "—";
}

// Normalize API response to unified list: [{ assignment_id, assigned_at, package, service, family: { family_id, family_head, members, address, finance_activated... } }]
function normalizeAssignedFamilies(raw) {
  if (!raw) return [];
  if (Array.isArray(raw?.families)) return raw.families;
  if (Array.isArray(raw)) {
    const looksWrapped = raw.length > 0 && raw[0] && typeof raw[0] === "object" && "family" in raw[0] && raw[0].family;
    if (looksWrapped) return raw;
    return raw.map((f) => ({
      assignment_id: f.assignment_id || f.assignmentId || null,
      assigned_at: f.assigned_at || f.assignedAt || null,
      package: f.package || { id: f.package_id, name: f.package_name, price: f.package_price },
      service: f.service || null,
      family: f.family || f,
    }));
  }
  if (Array.isArray(raw?.results)) return normalizeAssignedFamilies(raw.results);
  if (Array.isArray(raw?.data)) return normalizeAssignedFamilies(raw.data);
  if (raw?.family_id) return [{ assignment_id: raw.assignment_id || null, assigned_at: raw.assigned_at || null, package: raw.package || null, service: raw.service || null, family: raw }];
  return [];
}

export default function TechnicianMfiDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]); // normalized assignments
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [technicianInfo, setTechnicianInfo] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const selectedRef = useRef(null);

  // Smart Health Report state (per-member Generate Report — direct POST, no preview)
  const [generatingId, setGeneratingId] = useState(null);
  const [reportError, setReportError] = useState(null);
  const [retryMember, setRetryMember] = useState(null);
  const [reportSuccess, setReportSuccess] = useState(null);
  // Hidden off-screen report used only to render the PDF blob for direct upload
  const [hiddenReport, setHiddenReport] = useState(null);
  const [hiddenQr, setHiddenQr] = useState("");
  const hiddenReportRef = useRef(null);

  useEffect(() => {
    const name = localStorage.getItem("technicianName") || "";
    const email = localStorage.getItem("technicianEmail") || "";
    const id = localStorage.getItem("technicianId") || "";
    const isMfi = localStorage.getItem("is_mfi");
    setTechnicianInfo({ name, email, id, isMfi });
  }, []);

  const loadFamilies = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Not authenticated. Please login again.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const raw = await technicianMfiApi.getAssignedFamilies();
      const list = normalizeAssignedFamilies(raw);
      setItems(list);
      try { sessionStorage.setItem("mfi_assigned_cache", JSON.stringify(list)); } catch {}
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || e?.response?.data?.error || e.message || "Failed to load assigned families";
      setError(msg);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFamilies();
  }, [loadFamilies]);

  const isSameAssignment = (a, b) => {
    if (!a || !b) return false;
    const fa = a.family || a;
    const fb = b.family || b;
    if (fa?.family_id && fb?.family_id) return String(fa.family_id) === String(fb.family_id);
    if (a.assignment_id != null && b.assignment_id != null) return a.assignment_id === b.assignment_id;
    return a === b;
  };

  const handleView = (item) => {
    setSelectedItem((prev) => (prev && isSameAssignment(prev, item) ? null : item));
    // Keep the expanding row visible without jumping elsewhere on the page
    setTimeout(() => selectedRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 80);
  };

  const handleCloseSelected = () => {
    setSelectedItem(null);
  };

  const persistContext = (member) => {
    const family = selectedItem?.family;
    const pkg = selectedItem?.package || family?.package;
    const svc = selectedItem?.service || null;
    try {
      sessionStorage.setItem("mfi_current_family", JSON.stringify(family));
      sessionStorage.setItem("mfi_current_member", JSON.stringify(member));
      sessionStorage.setItem("mfi_current_package", JSON.stringify(pkg));
      sessionStorage.setItem("mfi_current_service", JSON.stringify(svc));
      sessionStorage.setItem("mfi_current_assignment", JSON.stringify(selectedItem));
    } catch {}
    return { family, pkg, svc };
  };

  const handleMfiServices = (member) => {
    const family = selectedItem?.family;
    const pkg = selectedItem?.package || family?.package;
    const familyId = family?.family_id;
    const memberId = member?.member_id;
    if (!familyId || !memberId) return;
    const { svc } = persistContext(member);
    // Pass numeric ids via query for MfiServices dynamic fetch (member.id + package.id)
    const memberNumericId = member?.id;
    const packageNumericId = pkg?.id;
    const qs = new URLSearchParams();
    if (memberNumericId) qs.set("memberId", String(memberNumericId));
    if (packageNumericId) qs.set("packageId", String(packageNumericId));
    const query = qs.toString() ? `?${qs.toString()}` : "";
    navigate(`/technician/mfi-services/${encodeURIComponent(familyId)}/${encodeURIComponent(memberId)}${query}`, {
      state: { family, member, package: pkg, service: svc, assignment: selectedItem },
    });
  };

  // const handleTele = (member) => {
  //   const family = selectedItem?.family;
  //   const pkg = selectedItem?.package || family?.package;
  //   const familyId = family?.family_id;
  //   const memberId = member?.member_id;
  //   if (!familyId || !memberId) return;
  //   const { svc } = persistContext(member);
  //   const memberNumericId = member?.id;
  //   const packageNumericId = pkg?.id;
  //   const qs = new URLSearchParams();
  //   if (memberNumericId) qs.set("memberId", String(memberNumericId));
  //   if (packageNumericId) qs.set("packageId", String(packageNumericId));
  //   const query = qs.toString() ? `?${qs.toString()}` : "";
  //   navigate(`/technician/tele-services/${encodeURIComponent(familyId)}/${encodeURIComponent(memberId)}${query}`, {
  //     state: { family, member, package: pkg, service: svc, assignment: selectedItem },
  //   });
  // };
  const handleTele = (member) => {
    const family = selectedItem?.family;
    const pkg = selectedItem?.package || family?.package;
    const familyId = family?.family_id;
    const memberId = member?.member_id;

    if (!familyId || !memberId) return;

    navigate(
      `/technician/tele-services/${encodeURIComponent(familyId)}/${encodeURIComponent(memberId)}`,
      {
        state: {
          family,
          member,
          package: pkg,
          service: selectedItem?.service || null,
          assignment: selectedItem,
        },
      }
    );
  };

  // Wait until the hidden off-screen report has committed to the DOM
  const waitForHiddenRender = async () => {
    for (let i = 0; i < 30; i++) {
      if (hiddenReportRef.current) return true;
      await new Promise((r) => setTimeout(r, 100));
    }
    return !!hiddenReportRef.current;
  };

  const handleGenerateReport = async (member) => {
    // Direct flow — NO preview modal:
    // 1) GET test-results by numeric member.id
    // 2) render SmartHealthReport hidden off-screen
    // 3) html2pdf → blob → POST report_file to
    //    POST /api/family-members/<member_id>/smart-report/
    //    (<member_id> = member.member_id string, NOT numeric id)
    const numericId = member?.id;
    if (numericId == null || numericId === "") {
      setReportError("Member id missing for this row. Please refresh and retry.");
      setRetryMember(member || null);
      return;
    }
    setGeneratingId(numericId);
    setReportError(null);
    setRetryMember(null);
    setReportSuccess(null);
    try {
      // Uses the app's existing authenticated axios client (Token/Bearer) + base URL config
      const data = await technicianMfiApi.getMemberTestResults(numericId);
      if (data && data.success === false) {
        throw new Error(data.message || data.detail || "Failed to load test results");
      }
      const results = Array.isArray(data?.results) ? data.results : [];
      if (results.length === 0) {
        setReportError("No test results available for this member");
        setRetryMember(member);
        return;
      }
      const normalized = normalizeSmartReport(data, member);
      if (!normalized || normalized.completedCount === 0) {
        setReportError("No test results available for this member");
        setRetryMember(member);
        return;
      }
      const reportMemberId = member?.member_id ?? normalized?.member?.member_id ?? null;
      if (reportMemberId == null || reportMemberId === "") {
        throw new Error("member_id missing for this row. Please refresh and retry.");
      }
      const fullReport = { ...normalized, memberNumericId: numericId, reportMemberId };

      // QR for page 1 (same content as the old preview modal)
      const rm = fullReport.member || {};
      const qrText = `${rm.member_id || ""} | ${rm.name || ""} | ${fullReport.screeningDate || ""}`;
      let qrUrl = "";
      try {
        qrUrl = await QRCode.toDataURL(qrText, { width: 220, margin: 1 });
      } catch {}
      setHiddenQr(qrUrl);
      setHiddenReport(fullReport);

      const rendered = await waitForHiddenRender();
      if (!rendered) throw new Error("Report render failed. Please retry.");
      try {
        await document.fonts.ready;
      } catch {}
      // Let QR/photo paint before snapshotting
      await new Promise((r) => setTimeout(r, 900));
      if (!hiddenReportRef.current) throw new Error("Report render failed. Please retry.");

      const filename = buildDirectReportFilename(fullReport);
      const pdfBlob = await html2pdf()
        .set({
          margin: 0,
          filename,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"] },
        })
        .from(hiddenReportRef.current)
        .outputPdf("blob");

      await technicianMfiApi.uploadMemberSmartReportPdf(reportMemberId, pdfBlob, filename);
      setReportSuccess(`Smart report saved for ${rm.name || reportMemberId}.`);
      setTimeout(() => setReportSuccess(null), 6000);
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e.message ||
        "Failed to generate report";
      setReportError(msg);
      setRetryMember(member);
    } finally {
      setGeneratingId(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("role");
    localStorage.removeItem("loginType");
    localStorage.removeItem("technicianId");
    localStorage.removeItem("technicianName");
    localStorage.removeItem("technicianEmail");
    localStorage.removeItem("is_medibuddy");
    localStorage.removeItem("is_mfi");
    localStorage.removeItem("is_northstar_technician");
    try { sessionStorage.removeItem("mfi_assigned_cache"); sessionStorage.removeItem("mfi_current_family"); sessionStorage.removeItem("mfi_current_member"); sessionStorage.removeItem("mfi_current_package"); sessionStorage.removeItem("mfi_current_service"); sessionStorage.removeItem("mfi_current_assignment"); } catch {}
    navigate("/login");
  };

  const selectedFamily = selectedItem?.family || null;

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const fam = item.family || item;
      const head = fam.family_head || {};
      const familyId = String(fam.family_id || "").toLowerCase();
      const headName = String(head.name || "").toLowerCase();
      const primaryMobile = String(fam.primary_mobile || "").toLowerCase();
      const headMobile = String(head.mobile || "").toLowerCase();
      const headLoan = String(head.loan_number || head.loanNumber || fam.loan_number || "").toLowerCase();
      const memberLoanMatch = (fam.members || []).some((m) => String(m.loan_number || "").toLowerCase().includes(q));
      return (
        familyId.includes(q) ||
        headName.includes(q) ||
        primaryMobile.includes(q) ||
        headMobile.includes(q) ||
        headLoan.includes(q) ||
        memberLoanMatch
      );
    });
  }, [items, searchQuery]);

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: COLORS.lightGrey }}>
      {/* Header */}
      <div style={{ backgroundColor: COLORS.white, padding: "16px 24px", borderBottom: `1px solid ${COLORS.mediumGrey}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: `${COLORS.vividPurple}18`, display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.vividPurple }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
          </span>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: COLORS.darkText, margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
              Technician MFI Dashboard
              <span style={{ fontSize: 11, backgroundColor: `${COLORS.vividPurple}15`, color: COLORS.vividPurple, padding: "3px 8px", borderRadius: 10, fontWeight: 700 }}>MFI</span>
            </h1>
            <p style={{ margin: "2px 0 0 0", fontSize: 12, color: COLORS.mediumText }}>
              {technicianInfo?.name ? `Welcome, ${technicianInfo.name}` : "MFI Assigned Families"}
              {technicianInfo?.email ? ` · ${technicianInfo.email}` : ""}
              {technicianInfo?.id ? ` · ID ${technicianInfo.id}` : ""}
            </p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={loadFamilies} style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 6, color: COLORS.darkText }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            Refresh
          </button>
          <button onClick={handleLogout} style={{ backgroundColor: COLORS.aquaBlue, color: COLORS.white, border: "none", borderRadius: 6, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Logout</button>
        </div>
      </div>

      <div style={{ flex: 1, padding: 24, maxWidth: 1200, width: "100%", margin: "0 auto", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Assigned Families Section */}
        <div style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 12, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: COLORS.darkText, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: COLORS.vividPurple, display: "inline-block" }} />
              Assigned Families
            </h2>
            {!loading && !error && (
              <span style={{ fontSize: 12, backgroundColor: `${COLORS.aquaBlue}12`, color: COLORS.aquaBlue, padding: "6px 12px", borderRadius: 20, fontWeight: 700 }}>
                {searchQuery ? `${filteredItems.length} / ${items.length}` : `${items.length}`} {items.length === 1 ? "family" : "families"}
              </span>
            )}
          </div>
          <div style={{ position: "relative", maxWidth: 520, width: "100%" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLORS.mediumText} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by Family ID, Head name, Mobile number, Loan ID..."
              style={{ width: "100%", padding: "9px 36px 9px 36px", borderRadius: 8, border: `1px solid ${COLORS.mediumGrey}`, fontSize: 13, outline: "none", backgroundColor: COLORS.white, color: COLORS.darkText, boxSizing: "border-box" }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", backgroundColor: COLORS.lightGrey, border: "none", borderRadius: 6, width: 22, height: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.mediumText, fontSize: 12 }}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 40, backgroundColor: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.mediumGrey}` }}>
            <div style={{ width: 36, height: 36, border: `4px solid ${COLORS.mediumGrey}`, borderTop: `4px solid ${COLORS.vividPurple}`, borderRadius: "50%", animation: "spin 1s linear infinite" }} />
            <p style={{ marginTop: 12, color: COLORS.mediumText, fontSize: 14 }}>Loading assigned families...</p>
          </div>
        ) : error ? (
          <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 20, textAlign: "center" }}>
            <p style={{ color: "#dc2626", fontSize: 14, margin: 0, fontWeight: 600 }}>Failed to load assigned families</p>
            <p style={{ color: "#dc2626", fontSize: 13, margin: "6px 0 0 0" }}>{error}</p>
            <button onClick={loadFamilies} style={{ marginTop: 12, backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "8px 16px", cursor: "pointer", fontSize: 13 }}>Retry</button>
          </div>
        ) : items.length === 0 ? (
          <div style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 12, padding: 40, textAlign: "center" }}>
            <div style={{ width: 48, height: 48, borderRadius: "50%", backgroundColor: `${COLORS.lightGrey}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px auto", color: COLORS.mediumText }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            </div>
            <p style={{ color: COLORS.mediumText, margin: 0, fontSize: 14, fontWeight: 600 }}>No families assigned yet</p>
            <p style={{ color: COLORS.lightText, margin: "6px 0 0 0", fontSize: 12 }}>Your MFI families will appear here after a coordinator assigns them to you.</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 12, padding: 32, textAlign: "center" }}>
            <p style={{ color: COLORS.mediumText, margin: 0, fontSize: 13, fontWeight: 600 }}>No families match your search</p>
            <p style={{ color: COLORS.lightText, margin: "6px 0 0 0", fontSize: 12 }}>Try searching by Family ID, Head name, Mobile or Loan ID</p>
            <button onClick={() => setSearchQuery("")} style={{ marginTop: 12, backgroundColor: COLORS.vividPurple, color: COLORS.white, border: "none", borderRadius: 6, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>Clear search</button>
          </div>
        ) : (
          <div style={{ backgroundColor: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.mediumGrey}`, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 600 }}>
                <thead>
                  <tr style={{ backgroundColor: COLORS.lightGrey, textAlign: "left", color: COLORS.mediumText }}>
                    <th style={{ padding: "10px 12px", fontWeight: 600, whiteSpace: "nowrap" }}>Family ID</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600 }}>Family Head</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600 }}>Mobile</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600, textAlign: "center" }}>Members</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600, textAlign: "center" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => {
                    const fam = item.family || item;
                    const isSelected = isSameAssignment(selectedItem, item);
                    const rowKey = fam.family_id || fam.id || item.assignment_id;
                    return (
                      <React.Fragment key={rowKey}>
                      <tr
                        onClick={() => handleView(item)}
                        style={{
                          borderTop: `1px solid ${COLORS.mediumGrey}`,
                          backgroundColor: isSelected ? `${COLORS.vividPurple}08` : COLORS.white,
                          cursor: "pointer",
                        }}
                        aria-expanded={isSelected}
                      >
                        <td style={{ padding: "12px", fontWeight: 700, color: COLORS.vividPurple, whiteSpace: "nowrap" }}>{fam.family_id || fam.id || "—"}</td>
                        <td style={{ padding: 12, color: COLORS.darkText }}>
                          <div style={{ fontWeight: 600 }}>{fam.family_head?.name || "—"}</div>
                          <div style={{ fontSize: 11, color: COLORS.lightText }}>{fam.family_head?.member_id || ""}</div>
                        </td>
                        <td style={{ padding: 12, color: COLORS.darkText, whiteSpace: "nowrap" }}>{fam.family_head?.mobile || fam.primary_mobile || "—"}</td>
                        <td style={{ padding: 12, textAlign: "center", color: COLORS.darkText, fontWeight: 600 }}>{fam.member_count ?? fam.members?.length ?? 0}</td>
                        <td style={{ padding: 12, textAlign: "center" }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleView(item); }}
                            style={{ backgroundColor: isSelected ? COLORS.darkGrey : COLORS.vividPurple, color: COLORS.white, border: "none", borderRadius: 6, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}
                          >
                            {isSelected ? "Hide" : "View"}
                          </button>
                        </td>
                      </tr>

                      {/* Family Members — inline drawer directly under the clicked row */}
                      {isSelected && selectedFamily && (
                        <tr style={{ backgroundColor: `${COLORS.vividPurple}05` }}>
                          <td colSpan={5} style={{ padding: 0, borderBottom: `1px solid ${COLORS.mediumGrey}` }}>
                            <div ref={selectedRef} style={{ padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>

            {/* Family Members TABLE — Family Information card removed per request */}
            <div style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 12, overflow: "hidden" }}>
              <div style={{ padding: "12px 14px", borderBottom: `1px solid ${COLORS.mediumGrey}`, backgroundColor: COLORS.lightGrey, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: COLORS.darkText, display: "flex", alignItems: "center", gap: 8 }}>
                  Family Members
                  <span style={{ fontSize: 11, backgroundColor: `${COLORS.vividPurple}12`, color: COLORS.vividPurple, padding: "3px 8px", borderRadius: 8, fontWeight: 700 }}>{selectedFamily.family_id}</span>
                </h3>
                <span style={{ fontSize: 11, color: COLORS.mediumText }}>{selectedFamily.members?.length || 0} members · horizontal scroll on mobile</span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 1020 }}>
                  <thead>
                    <tr style={{ backgroundColor: COLORS.lightGrey, textAlign: "left", color: COLORS.mediumText }}>
                      <th style={{ padding: "10px 12px", fontWeight: 600, whiteSpace: "nowrap" }}>Member ID</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600 }}>Name</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600 }}>Relationship</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, textAlign: "center" }}>Age</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, textAlign: "center" }}>Gender</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, whiteSpace: "nowrap" }}>Mobile</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, textAlign: "center", whiteSpace: "nowrap" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedFamily.members || []).map((m) => {
                      return (
                      <tr key={m.member_id || m.id} style={{ borderTop: `1px solid ${COLORS.mediumGrey}`, backgroundColor: COLORS.white }}>
                        <td style={{ padding: "10px 12px", fontWeight: 600, color: COLORS.darkText, whiteSpace: "nowrap" }}>{m.member_id || m.id || "—"}</td>
                        <td style={{ padding: "10px 12px", color: COLORS.darkText }}>
                          <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                            {m.name}
                            {m.is_primary_contact && <span style={{ fontSize: 10, backgroundColor: `${COLORS.vividPurple}15`, color: COLORS.vividPurple, padding: "2px 6px", borderRadius: 10, fontWeight: 700 }}>HEAD</span>}
                          </div>
                        </td>
                        <td style={{ padding: "10px 12px", color: COLORS.mediumText, textTransform: "capitalize" }}>{m.relationship || "—"}</td>
                        <td style={{ padding: "10px 12px", color: COLORS.mediumText, textAlign: "center" }}>{m.age ?? "—"}</td>
                        <td style={{ padding: "10px 12px", color: COLORS.mediumText, textAlign: "center" }}>{m.gender || "—"}</td>
                        <td style={{ padding: "10px 12px", color: COLORS.darkText, whiteSpace: "nowrap" }}>{m.mobile || "Not available"}</td>
                        <td style={{ padding: "10px 12px" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, flexWrap: "wrap" }}>
                            <button
                              onClick={() => handleMfiServices(m)}
                              style={{ backgroundColor: COLORS.aquaBlue, color: COLORS.white, border: "none", borderRadius: 6, padding: "7px 12px", cursor: "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
                            >
                              MFI Services
                            </button>
                            <button
                              onClick={() => handleTele(m)}
                              style={{ backgroundColor: COLORS.darkGrey, color: COLORS.white, border: "none", borderRadius: 6, padding: "7px 12px", cursor: "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
                            >
                              Tele
                            </button>
                            <button
                              onClick={() => handleGenerateReport(m)}
                              disabled={generatingId === m.id}
                              title="Generate Report"
                              style={{ backgroundColor: generatingId === m.id ? COLORS.mediumGrey : "#13294b", color: COLORS.white, border: "none", borderRadius: 6, padding: "7px 12px", cursor: generatingId === m.id ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6, opacity: generatingId === m.id ? 0.75 : 1 }}
                            >
                              {generatingId === m.id ? (
                                <>
                                  <span style={{ width: 12, height: 12, border: "2px solid #ffffff", borderTop: "2px solid transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
                                  Generating…
                                </>
                              ) : (
                                <>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path strokeLinecap="round" strokeLinejoin="round" d="M14 2v6h6M9 13h6M9 17h6" /></svg>
                                  Generate Report
                                </>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );})}
                    {(!selectedFamily.members || selectedFamily.members.length === 0) && (
                      <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: COLORS.mediumText, fontSize: 13 }}>No members found for this family.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: "10px 14px", borderTop: `1px solid ${COLORS.mediumGrey}`, display: "flex", justifyContent: "flex-end", backgroundColor: COLORS.white }}>
                <button onClick={handleCloseSelected} style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600, color: COLORS.mediumText }}>Back / Close</button>
              </div>
            </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      {/* Report error toast with retry */}
      {reportError && (
        <div style={{ position: "fixed", top: 16, right: 16, zIndex: 3000, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 12px", maxWidth: 360, boxShadow: "0 6px 20px rgba(0,0,0,0.15)", display: "flex", gap: 8, alignItems: "flex-start" }}>
          <span style={{ fontSize: 16 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#b91c1c" }}>Report failed</div>
            <div style={{ fontSize: 12, color: "#7f1d1d", marginTop: 2 }}>{reportError}</div>
            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
              {retryMember && (
                <button
                  onClick={() => handleGenerateReport(retryMember)}
                  style={{ background: "#b91c1c", color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}
                >
                  Retry
                </button>
              )}
              <button
                onClick={() => { setReportError(null); setRetryMember(null); }}
                style={{ background: "#fff", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Hidden off-screen report render — used only to build the PDF blob for
          direct POST (no preview modal). Kept off-viewport so html2canvas can
          still snapshot it with layout intact. */}
      <div aria-hidden="true" style={{ position: "fixed", left: -10000, top: 0, width: 794, pointerEvents: "none" }}>
        {hiddenReport && (
          <SmartHealthReport ref={hiddenReportRef} report={hiddenReport} qrDataUrl={hiddenQr} />
        )}
      </div>
      {/* Report success toast */}
      {reportSuccess && (
        <div style={{ position: "fixed", top: 16, right: 16, zIndex: 3000, background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 10, padding: "10px 12px", maxWidth: 360, boxShadow: "0 6px 20px rgba(0,0,0,0.15)", display: "flex", gap: 8, alignItems: "flex-start" }}>
          <span style={{ fontSize: 16 }}>✓</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#065f46" }}>Report saved</div>
            <div style={{ fontSize: 12, color: "#047857", marginTop: 2 }}>{reportSuccess}</div>
            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
              <button
                onClick={() => setReportSuccess(null)}
                style={{ background: "#fff", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
